import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  notificationsApi,
  NotificationListItem,
  NotificationStatus,
  NotificationType,
  ListNotificationsParams,
  ArchivedFilter,
  SourceFilter,
} from '../../api/notifications';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Plus,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Archive,
  ArchiveRestore,
  Trash2,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useConfirm } from '../../components/shared/ConfirmDialog';

interface NotificationsPageProps {
  isDark: boolean;
}

const ITEMS_PER_PAGE = 20;

/**
 * Человеческие названия триггерных кампаний. Метка приходит с бэкенда как
 * машинный ключ из `data.campaign`; незнакомый ключ показываем как есть, чтобы
 * новая кампания не оставалась безымянной до правки админки.
 */
const CAMPAIGN_LABELS: Record<string, string> = {
  abandoned_order: 'Незавершённая оплата',
};

export default function NotificationsPage({ isDark }: NotificationsPageProps) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<ListNotificationsParams>({ page: 1, limit: ITEMS_PER_PAGE, archived: 'all', source: 'all' });
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await notificationsApi.list({ ...filters, page: currentPage });
      setNotifications(data.notifications);
      setTotalPages(data.pagination.total_pages);
      setTotal(data.pagination.total);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось загрузить уведомления');
    } finally {
      setIsLoading(false);
    }
  }, [filters, currentPage]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const getStatusBadge = (status: NotificationStatus) => {
    const variants: Record<NotificationStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
      [NotificationStatus.PENDING]: { variant: 'secondary', label: 'Ожидает' },
      [NotificationStatus.SCHEDULED]: { variant: 'default', label: 'Запланировано' },
      [NotificationStatus.SENT]: { variant: 'default', label: 'Отправлено' },
      [NotificationStatus.FAILED]: { variant: 'destructive', label: 'Ошибка' },
      [NotificationStatus.CANCELLED]: { variant: 'outline', label: 'Отменено' },
    };
    const config = variants[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeBadge = (type: NotificationType) => {
    const colors: Record<NotificationType, string> = {
      [NotificationType.SYSTEM]: 'bg-blue-500',
      [NotificationType.PAYMENT]: 'bg-green-500',
      [NotificationType.SUBSCRIPTION]: 'bg-purple-500',
      [NotificationType.PROMO]: 'bg-orange-500',
      [NotificationType.REMINDER]: 'bg-yellow-500',
      [NotificationType.ACHIEVEMENT]: 'bg-pink-500',
    };
    return <Badge className={cn('text-white', colors[type])}>{type.charAt(0).toUpperCase() + type.slice(1)}</Badge>;
  };

  const handleCancel = async (id: string) => {
    const ok = await confirm({
      title: 'Отменить запланированное уведомление?',
      description: 'Уведомление не будет отправлено.',
      confirmLabel: 'Отменить рассылку',
      destructive: true,
    });
    if (!ok) return;
    try {
      await notificationsApi.cancel(id);
      toast.success('Уведомление отменено');
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось отменить уведомление');
    }
  };

  const handleArchive = async (notification: NotificationListItem) => {
    const ok = await confirm({
      title: 'Скрыть уведомление из приложения?',
      description:
        'Пользователи перестанут его видеть, счётчик непрочитанных пересчитается. ' +
        'Статистика доставки и прочтений сохранится, вернуть можно в любой момент.',
      confirmLabel: 'Скрыть',
    });
    if (!ok) return;
    setBusyId(notification.id);
    try {
      await notificationsApi.archive(notification.id);
      toast.success('Уведомление скрыто из приложения');
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось скрыть уведомление');
    } finally {
      setBusyId(null);
    }
  };

  const handleUnarchive = async (notification: NotificationListItem) => {
    setBusyId(notification.id);
    try {
      await notificationsApi.unarchive(notification.id);
      toast.success('Уведомление снова видно пользователям');
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось вернуть уведомление');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (notification: NotificationListItem) => {
    // Отдельно предупреждаем про статистику: у отправленной рассылки это
    // единственный след того, что произошло, и восстановить его нечем.
    const wasDelivered = notification.recipients_count > 0;
    const ok = await confirm({
      title: 'Удалить уведомление навсегда?',
      description: wasDelivered
        ? `Вместе с уведомлением исчезнут получатели (${notification.recipients_count.toLocaleString('ru-RU')}), ` +
          'попытки доставки и вся статистика прочтений. Отменить это будет нельзя. ' +
          'Если нужно только убрать сообщение у пользователей — используйте «Скрыть».'
        : 'Уведомление будет удалено безвозвратно.',
      confirmLabel: 'Удалить навсегда',
      destructive: true,
    });
    if (!ok) return;
    setBusyId(notification.id);
    try {
      await notificationsApi.delete(notification.id);
      toast.success('Уведомление удалено');
      fetchNotifications();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось удалить уведомление');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn('text-2xl lg:text-3xl font-bold tracking-tight', isDark ? 'text-white' : 'text-[#111111]')}>Уведомления</h1>
          <p className={cn('text-sm mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>Управление push-уведомлениями</p>
        </div>
        <Button onClick={() => navigate('/notifications/create')} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
          <Plus className="w-4 h-4 mr-2" />
          Создать уведомление
        </Button>
      </div>

      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="relative">
              <Search className={cn('absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
              <Input placeholder="Поиск уведомлений..." value={filters.search || ''} onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className={cn('pl-10', isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')} />
            </div>
            <Select value={filters.status || 'all'} onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? undefined : (value as NotificationStatus), page: 1 })}>
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value={NotificationStatus.PENDING}>Ожидает</SelectItem>
                <SelectItem value={NotificationStatus.SCHEDULED}>Запланировано</SelectItem>
                <SelectItem value={NotificationStatus.SENT}>Отправлено</SelectItem>
                <SelectItem value={NotificationStatus.FAILED}>Ошибка</SelectItem>
                <SelectItem value={NotificationStatus.CANCELLED}>Отменено</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.type || 'all'} onValueChange={(value) => setFilters({ ...filters, type: value === 'all' ? undefined : (value as NotificationType), page: 1 })}>
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue placeholder="Все типы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все типы</SelectItem>
                <SelectItem value={NotificationType.SYSTEM}>Система</SelectItem>
                <SelectItem value={NotificationType.PAYMENT}>Платёж</SelectItem>
                <SelectItem value={NotificationType.SUBSCRIPTION}>Подписка</SelectItem>
                <SelectItem value={NotificationType.PROMO}>Промо</SelectItem>
                <SelectItem value={NotificationType.REMINDER}>Напоминание</SelectItem>
                <SelectItem value={NotificationType.ACHIEVEMENT}>Достижение</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.archived || 'all'} onValueChange={(value) => setFilters({ ...filters, archived: value as ArchivedFilter, page: 1 })}>
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue placeholder="Видимость" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все уведомления</SelectItem>
                <SelectItem value="active">Видны в приложении</SelectItem>
                <SelectItem value="archived">Скрытые</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.source || 'all'} onValueChange={(value) => setFilters({ ...filters, source: value as SourceFilter, page: 1 })}>
              <SelectTrigger className={cn(isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white')}>
                <SelectValue placeholder="Источник" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Любой источник</SelectItem>
                <SelectItem value="manual">Созданные вручную</SelectItem>
                <SelectItem value="auto">Автоматические</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setFilters({ page: 1, limit: ITEMS_PER_PAGE, archived: 'all', source: 'all' }); setCurrentPage(1); }}
              className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
              Сбросить фильтры
            </Button>
          </div>
        </div>
      </Card>

      <Card className={cn(isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
        {isLoading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-[#C9A227]" /></div>
        ) : notifications.length === 0 ? (
          <div className="text-center p-12"><p className={cn('text-lg', isDark ? 'text-slate-400' : 'text-slate-500')}>Уведомления не найдены</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={cn(isDark ? 'border-slate-700 hover:bg-slate-800' : 'hover:bg-slate-50')}>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Заголовок</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Тип</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Статус</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Прогноз</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Адресовано / push</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>% прочтений</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Создано</TableHead>
                    <TableHead className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notification) => (
                    <TableRow key={notification.id} className={cn(isDark ? 'border-slate-700 hover:bg-slate-800' : 'hover:bg-slate-50')}>
                      <TableCell className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
                        <div>
                          <div className="font-medium">{notification.title}</div>
                          <div className={cn('text-xs mt-1 truncate max-w-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>{notification.body}</div>
                          {notification.campaign && (
                            <Badge variant="outline" className="gap-1 mt-2">
                              <Bot className="w-3 h-3" />
                              {CAMPAIGN_LABELS[notification.campaign] || notification.campaign}
                              {notification.campaign_period ? ` · ${notification.campaign_period}` : ''}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getTypeBadge(notification.type)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          {getStatusBadge(notification.status)}
                          {notification.is_archived && (
                            <Badge variant="outline" className="gap-1">
                              <Archive className="w-3 h-3" />
                              Скрыто
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{notification.estimated_recipients?.toLocaleString() || '-'}</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{notification.recipients_count.toLocaleString('ru-RU')} / {notification.push_sent_count.toLocaleString('ru-RU')}</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{notification.read_rate.toFixed(1)}%</TableCell>
                      <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-600')}>{format(new Date(notification.created_at), 'MMM d, yyyy HH:mm')}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" aria-label="Аналитика уведомления" onClick={() => navigate(`/notifications/${notification.id}/analytics`)}
                            className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : '')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {notification.status === NotificationStatus.SCHEDULED && (
                            <Button variant="ghost" size="sm" aria-label="Отменить рассылку" onClick={() => handleCancel(notification.id)}
                              className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : '')}>
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          {notification.is_archived ? (
                            <Button variant="ghost" size="sm" aria-label="Вернуть в приложение" title="Вернуть в приложение"
                              disabled={busyId === notification.id}
                              onClick={() => handleUnarchive(notification)}
                              className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : '')}>
                              <ArchiveRestore className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" aria-label="Скрыть из приложения" title="Скрыть из приложения"
                              disabled={busyId === notification.id}
                              onClick={() => handleArchive(notification)}
                              className={cn(isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : '')}>
                              <Archive className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" aria-label="Удалить навсегда" title="Удалить навсегда"
                            disabled={busyId === notification.id}
                            onClick={() => handleDelete(notification)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-500/10">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className={cn('flex items-center justify-between p-4 border-t', isDark ? 'border-slate-700' : 'border-slate-200')}>
              <div className={cn('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, total)} of {total} notifications
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className={cn('text-sm px-3', isDark ? 'text-slate-300' : 'text-slate-600')}>Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className={cn(isDark ? 'border-slate-600 text-white hover:bg-slate-700' : '')}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
